import { action, atom } from '@reatom/framework'
import { BigNumber } from 'bignumber.js'

import { Order } from '@blade/std-lib'
import { CurrencyAddress, CurrencyModel } from '@root/entities/currency'

import { PairAddress } from '../model'

import type { Atom } from '@reatom/framework'
import type { Network } from '@root/entities/network'
import type { ServicesApi } from '@root/shared/api'
import type { TokenLogosHost } from '@root/shared/models'

export interface PairCreatorStats {
  totalPools: number
  successfulPools: number
  ruggedPools: number
  isDangerous: boolean
  bondedPools?: number
  bondedPercent?: number
  created7d?: number
  solBalance?: BigNumber
  topTokens: Array<PairCreatorStatsTopToken>
  lastTokenLaunchedAt?: Date
  updatedAt: Date
}

export interface PairCreatorStatsTopToken {
  address: CurrencyAddress
  pairAddress: PairAddress | undefined
  currencyModel: CurrencyModel
  currentMarketCap: BigNumber
  athMarketCap: BigNumber
  traders24h: number
  initTimestamp: Date
}

export interface PairCreatorStatsModel
  extends Atom<PairCreatorStats | undefined> {}

export const fromDto = (
  dto: NonNullable<ServicesApi.PairCreatorStatsDto>,
  imagesHost: TokenLogosHost,
  network: Network.Network,
): PairCreatorStats => {
  return {
    totalPools: dto.totalPools,
    successfulPools: dto.successfulPools,
    ruggedPools: dto.ruggedPools,
    isDangerous: dto.isDangerous,
    bondedPools: dto.bondedPools,
    bondedPercent: dto.bondedPercent,
    created7d: dto.created7d,
    solBalance: dto.solBalance ? BigNumber(dto.solBalance) : undefined,
    topTokens:
      dto.topTokens?.map((x) => {
        const currencyModel = CurrencyModel.fromPairCreatorStatsTokenDto(
          x,
          imagesHost,
          network,
        )
        return {
          address: CurrencyAddress(x.address),
          pairAddress: x.pairAddress ? PairAddress(x.pairAddress) : undefined,
          currencyModel,
          currentMarketCap: BigNumber(x.currentMarketCap),
          athMarketCap: BigNumber(x.athMarketCap),
          traders24h: x.traders24h,
          initTimestamp: new Date(x.initTimestamp),
        }
      }) ?? [],

    lastTokenLaunchedAt: dto.lastTokenLaunchedAt
      ? new Date(dto.lastTokenLaunchedAt)
      : undefined,
    updatedAt: new Date(dto.updatedAt),
  }
}

export const reatomPairCreatorStats = (
  dto: ServicesApi.PairDto['creatorStats'],
  imagesHost: TokenLogosHost,
  network: Network.Network,
) => {
  //eslint-disable-next-line @reatom/unit-naming-rule
  const atom_ = atom<PairCreatorStats | undefined>(
    dto === undefined ? undefined : fromDto(dto, imagesHost, network),
  )
  return {
    atom: atom_,
    updateFromDtoAction: action(
      (ctx, dto: NonNullable<ServicesApi.PairDto['creatorStats']>) => {
        const current = ctx.get(atom_)

        if (
          current &&
          Order.isGreaterThanOrEqualTo(Order.Date)(
            current.updatedAt,
            new Date(dto.updatedAt),
          )
        ) {
          return
        }

        const model = fromDto(dto, imagesHost, network)
        const updated: PairCreatorStats = {
          ...model,
          lastTokenLaunchedAt:
            model.lastTokenLaunchedAt ?? current?.lastTokenLaunchedAt,
          solBalance: model.solBalance ?? current?.solBalance,
        }

        atom_(ctx, updated)
      },
      'reatomPairCreatorStats.updateFromDtoAction',
    ),
  }
}
